import { DigitalAuditLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  AccountSearchResult,
  AdvertiserType,
  AdvertiserTypeResponse,
  BrandInput,
  CompanyGoogleAdsAccount,
  CompanyMcc,
  CreateAuditRunParams,
  CreateAuditRunResponse,
  GetAuditRunResponse,
  HydratedBrandResponse,
  AuditRunListItem
} from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';

export const getCompanyMccs = async (company: string): Promise<CompanyMcc[]> => {
  const res = await DigitalAuditLambdaFetch('/google-ads-audit/mccs', { params: { company } });
  return awaitJSON<CompanyMcc[]>(res);
};

export const getCompanyAccounts = async (company: string): Promise<CompanyGoogleAdsAccount[]> => {
  const res = await DigitalAuditLambdaFetch('/google-ads-audit/connected-accounts', {
    params: { company }
  });
  return awaitJSON<CompanyGoogleAdsAccount[]>(res);
};

export const searchAccounts = async (mccId: string, q?: string): Promise<AccountSearchResult[]> => {
  const res = await DigitalAuditLambdaFetch('/google-ads-audit/accounts', {
    params: { mccId, ...(q ? { q } : {}) }
  });
  return awaitJSON<AccountSearchResult[]>(res);
};

export const listAuditRuns = async (company: string): Promise<AuditRunListItem[]> => {
  const res = await DigitalAuditLambdaFetch('/google-ads-audit/audit', { params: { company } });
  return awaitJSON<AuditRunListItem[]>(res);
};

// Only creates the run row — the audit itself is submitted server-side as an AWS Batch
// job (see auditRuns.ts's createAuditRun in bpm-lambdas), so there is nothing to kick off
// from here. Run status lives in Postgres and is polled via getAuditRun.
export const createAuditRun = async (
  company: string,
  input: CreateAuditRunParams
): Promise<CreateAuditRunResponse> => {
  const res = await DigitalAuditLambdaFetch('/google-ads-audit/audit', {
    method: 'POST',
    body: { company, ...input }
  });
  return awaitJSON<CreateAuditRunResponse>(res);
};

export const getAuditRun = async (company: string, runId: string): Promise<GetAuditRunResponse> => {
  const res = await DigitalAuditLambdaFetch(`/google-ads-audit/audit/${runId}`, {
    params: { company }
  });
  return awaitJSON<GetAuditRunResponse>(res);
};

export const getAdvertiserType = async (
  company: string,
  accountId: string
): Promise<AdvertiserTypeResponse> => {
  const res = await DigitalAuditLambdaFetch(
    `/google-ads-audit/accounts/${accountId}/advertiser-type`,
    {
      params: { company }
    }
  );
  return awaitJSON<AdvertiserTypeResponse>(res);
};

export const setAdvertiserType = async (
  company: string,
  accountId: string,
  advertiserType: AdvertiserType,
  accountName?: string
): Promise<AdvertiserTypeResponse> => {
  const res = await DigitalAuditLambdaFetch(
    `/google-ads-audit/accounts/${accountId}/advertiser-type`,
    {
      method: 'POST',
      body: { company, advertiserType, accountName }
    }
  );
  return awaitJSON<AdvertiserTypeResponse>(res);
};

export const getAccountBrand = async (
  company: string,
  accountId: string,
  name?: string
): Promise<HydratedBrandResponse> => {
  const res = await DigitalAuditLambdaFetch(`/google-ads-audit/accounts/${accountId}/brand`, {
    params: { company, ...(name ? { name } : {}) }
  });
  return awaitJSON<HydratedBrandResponse>(res);
};

export const updateAccountBrand = async (
  company: string,
  accountId: string,
  input: BrandInput
): Promise<HydratedBrandResponse['brand']> => {
  const res = await DigitalAuditLambdaFetch(`/google-ads-audit/accounts/${accountId}/brand`, {
    method: 'PUT',
    body: { company, ...input }
  });
  return awaitJSON<HydratedBrandResponse['brand']>(res);
};
